import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { defaultConfig } from 'src/app/app.config';
import { UserSessionService } from '@services/user-session.service';

/**
 * Bug 275470 — datos del retiro que acompañan a cada etapa para alimentar la tabla descargable
 * de la HU 206321. Todos opcionales: una etapa temprana no conoce aún la causal ni el valor.
 */
export interface WithdrawalDetail {
  documentType?: string;
  documentNumber?: string;
  requestNumber?: string;
  account?: string;
  withdrawalCause?: string;
  paymentType?: string;
  amount?: string;
  errorMessage?: string;
}

const ANALYTICS_NAMESPACE = 'mobileAnalytics';
const APP_VERSION = '2.5.0';
// Bug 267021 — el navegador no expone un UUID de hardware; se genera y persiste uno
// estable por dispositivo/navegador en localStorage para identificar el dispositivo en
// el reporte operacional/mensual, de forma equivalente al UUID de hardware en movil.
const DEVICE_UUID_STORAGE_KEY = 'fna_device_uuid';

@Injectable({ providedIn: 'root' })
export class AnalyticsTrackerService {
  private http = inject(HttpClient);
  private userSession = inject(UserSessionService);
  private apiRoot = (defaultConfig as any)?.apiRootPath || '/ws-affilliate/';
  private userId = '';
  private userType = 'AFILIADO';
  private deviceUuid = this.resolveDeviceUuid();
  // Bug 266163 (cierre de brecha sessionId) — copia propia del sessionId: el store de
  // UserSessionService se llena DESPUÉS de LOGIN_SUCCESS/SESSION_STARTED y se limpia
  // ANTES de SESSION_ENDED/LOGOUT_EXECUTED, por lo que leerlo solo al momento de
  // trackear dejaba esos eventos sin sesión. El login la fija apenas conoce el
  // sessionHandler y el cierre la limpia después de trackear el logout.
  private sessionId: string | null = null;
  private deviceInfo = this.detectDevice();

  setUser(userId: string, userType?: string) {
    this.userId = userId;
    if (userType) {
      this.userType = userType;
    }
  }

  setSession(sessionId: string | null) {
    this.sessionId = sessionId;
  }

  private resolveDeviceUuid(): string {
    try {
      const stored = localStorage.getItem(DEVICE_UUID_STORAGE_KEY);
      if (stored) {
        return stored;
      }
      const generated =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(DEVICE_UUID_STORAGE_KEY, generated);
      return generated;
    } catch {
      // localStorage no disponible (modo privado/SSR): el UUID es best-effort.
      return '';
    }
  }

  private detectDevice(): { deviceType: string; deviceVersion: string } {
    const ua = navigator?.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) {
      const match = ua.match(/OS (\d+[_.]\d+)/);
      return { deviceType: 'IOS', deviceVersion: match ? match[1].replace('_', '.') : '' };
    }
    if (/Android/.test(ua)) {
      const match = ua.match(/Android (\d+[.\d]*)/);
      return { deviceType: 'ANDROID', deviceVersion: match ? match[1] : '' };
    }
    // Bug 266165 — en escritorio se enviaba la versión vacía, así que el Explorador de Sesiones
    // mostraba «—» en «Versión disp.» para toda sesión web. El equivalente a la versión del
    // dispositivo en un navegador es la del sistema operativo, y si no se puede leer, la del
    // propio navegador; así la columna deja de estar en blanco.
    return { deviceType: 'WEB', deviceVersion: this.detectWebVersion(ua) };
  }

  /** Versión del sistema operativo (o, en su defecto, del navegador) leída del user agent. */
  private detectWebVersion(ua: string): string {
    const os = [
      { re: /Windows NT ([\d.]+)/, name: 'Windows' },
      { re: /Mac OS X (\d+[_.\d]*)/, name: 'macOS' },
      { re: /CrOS \w+ ([\d.]+)/, name: 'ChromeOS' },
    ];
    for (const { re, name } of os) {
      const match = ua.match(re);
      if (match) {
        return `${name} ${match[1].replace(/_/g, '.')}`;
      }
    }
    if (/Linux/.test(ua)) {
      return 'Linux';
    }
    const browser = ua.match(/(Edg|OPR|Chrome|Firefox|Version)\/([\d.]+)/);
    return browser ? `${browser[1] === 'Version' ? 'Safari' : browser[1]} ${browser[2]}` : '';
  }

  private deriveResult(attrs: Record<string, any>): string {
    if (attrs['result']) {
      return attrs['result'];
    }
    if (attrs['success'] === true) {
      return 'SUCCESS';
    }
    if (attrs['success'] === false) {
      return 'FAILURE';
    }
    return 'SUCCESS';
  }

  private track(eventClass: string, eventType: string, attrs: Record<string, any> = {}) {
    if (!this.apiRoot) {
      return;
    }
    const now = new Date();
    const uid = attrs['userId'] || this.userId || 'anonymous';
    const sessionId = this.sessionId || this.userSession.getSessionData()?.sessionId || null;
    const event = {
      nameSpace: ANALYTICS_NAMESPACE,
      eventClass,
      eventType,
      date: Math.floor(now.getTime() / 1000),
      fulldate: now.toISOString(),
      author: uid,
      detail: `${eventClass}/${eventType}`,
      sessionId,
      channel: 'WEB_APP',
      attributes: {
        userId: uid,
        userType: attrs['userType'] || this.userType,
        sessionId,
        channel: 'WEB_APP',
        result: this.deriveResult(attrs),
        deviceType: this.deviceInfo.deviceType,
        deviceVersion: this.deviceInfo.deviceVersion,
        deviceUUID: this.deviceUuid,
        os: this.deviceInfo.deviceType,
        appVersion: APP_VERSION,
        ...attrs,
      },
    };
    this.http
      .post(`${this.apiRoot}analytics/registerEvent`, { data: event })
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  trackLoginSuccess(userId?: string, userType?: string) {
    if (userId) {
      this.setUser(userId, userType);
    }
    this.track('AUTH', 'LOGIN_SUCCESS', { success: true, product: 'GENERAL' });
  }

  trackLoginFailed(userId?: string, errorCode?: string) {
    this.track('AUTH', 'LOGIN_FAILED', {
      userId: userId || this.userId,
      success: false,
      errorCode,
      product: 'GENERAL',
    });
  }

  trackLoginUserNoProducts(userId?: string) {
    this.track('AUTH', 'LOGIN_USER_NO_PRODUCTS', { userId: userId || this.userId, success: false, product: 'GENERAL' });
  }

  trackLoginUserPrelinked(userId?: string) {
    this.track('AUTH', 'LOGIN_USER_PRELINKED', { userId: userId || this.userId, success: false, product: 'GENERAL' });
  }

  trackLoginUserIncompleteData(userId?: string) {
    this.track('AUTH', 'LOGIN_USER_INCOMPLETE_DATA', {
      userId: userId || this.userId,
      success: false,
      product: 'GENERAL',
    });
  }

  trackOtpUserNotFoundCobis(userId?: string) {
    this.track('AUTH', 'OTP_USER_NOT_FOUND_COBIS', {
      userId: userId || this.userId,
      success: false,
      product: 'GENERAL',
    });
  }

  // Bug 266110 — el reporte de auditoría 2FA requiere capturar el destino del OTP
  // (correo/celular al que se envió), el código de sistema devuelto por el backend
  // y un mensaje de error legible. Se omite cada llave cuando su valor está vacío.
  track2faRequested(method: string, destination?: string, userId?: string) {
    this.track('2FA', '2FA_REQUESTED', {
      authenticationMethod: method,
      ...(destination ? { destination } : {}),
      ...(userId ? { userId } : {}),
      product: 'GENERAL',
    });
  }

  track2faSuccess(method: string, opts: { destination?: string; systemCode?: string; userId?: string } = {}) {
    this.track('2FA', '2FA_SUCCESS', {
      authenticationMethod: method,
      success: true,
      ...(opts.destination ? { destination: opts.destination } : {}),
      ...(opts.systemCode ? { systemCode: opts.systemCode } : {}),
      ...(opts.userId ? { userId: opts.userId } : {}),
      product: 'GENERAL',
    });
  }

  track2faFailed(
    method: string,
    errorCode?: string,
    opts: { destination?: string; systemCode?: string; errorMessage?: string; userId?: string } = {}
  ) {
    this.track('2FA', '2FA_FAILED', {
      authenticationMethod: method,
      success: false,
      errorCode,
      ...(opts.destination ? { destination: opts.destination } : {}),
      ...(opts.systemCode ? { systemCode: opts.systemCode } : {}),
      ...(opts.errorMessage ? { errorMessage: opts.errorMessage } : {}),
      ...(opts.userId ? { userId: opts.userId } : {}),
      product: 'GENERAL',
    });
  }

  trackFeatureOpened(featureName: string) {
    this.track('FUNCTIONAL', featureName, { module: featureName, product: 'GENERAL' });
  }

  trackLogout() {
    this.track('FUNCTIONAL', 'LOGOUT_EXECUTED', { success: true, product: 'GENERAL' });
  }

  /**
   * Bug 275470 — la tabla descargable de la HU 206321 pide doce columnas (proceso, documento,
   * número de solicitud, cuenta, causal, tipo de pago, valor, código, fecha, etapa y error) y la
   * etapa solo viajaba con el producto y el resultado, así que seis columnas no existían y otras
   * tres salían en blanco. `detalle` lleva lo que el trámite ya conoce en ese punto; lo que aún
   * no se ha diligenciado simplemente no se envía.
   */
  trackCesantiasStep(step: string, success = true, errorCode?: string, detalle: WithdrawalDetail = {}) {
    this.track('WITHDRAWAL', step, { product: 'CESANTIAS', success, errorCode, ...detalle });
  }

  trackAvcStep(step: string, success = true, errorCode?: string, detalle: WithdrawalDetail = {}) {
    this.track('WITHDRAWAL', step, { product: 'AVC', success, errorCode, ...detalle });
  }

  trackCreditStep(step: string, success = true, errorCode?: string) {
    this.track('CREDIT', step, { product: 'CREDITO', success, errorCode });
  }

  trackLoginStarted(userId?: string) {
    this.track('AUTH', 'LOGIN_STARTED', { userId: userId || this.userId, result: 'IN_PROGRESS', product: 'GENERAL' });
  }

  trackSessionStarted() {
    this.track('SESSION', 'SESSION_STARTED', { result: 'SUCCESS', product: 'GENERAL' });
  }

  trackSessionEnded(reason: string) {
    this.track('SESSION', 'SESSION_ENDED', { result: 'SUCCESS', reason, product: 'GENERAL' });
  }

  // HU 210464 — registro de uso de guías rápidas: paso, tipo de recurso y resultado.
  trackGuideUsage(step: string, resourceType: string, result: 'SUCCESS' | 'FAILURE' = 'SUCCESS') {
    this.track('AFFILIATION_GUIDE', 'GUIDE_RESOURCE_OPENED', {
      step,
      resourceType,
      result,
      product: 'AFFILIATION',
    });
  }
}
